import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { supabase } from '@/lib/supabase/client';

const searchSchema = z
  .object({
    ws: z.string().uuid().optional(),
    role: z.enum(['admin', 'member', 'guest']).optional(),
  })
  .partial();

export const Route = createFileRoute('/_auth/accept-invite')({
  validateSearch: searchSchema,
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const search = useSearch({ from: '/_auth/accept-invite' });
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // Esperar a que detectSessionInUrl hidrate la sesion (magic link intercambia el token).
      const { data: sessData, error: sessErr } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sessErr || !sessData.session) {
        setStatus('error');
        setMessage('Sesión inválida. Abre el link desde el correo nuevamente.');
        toast.error('No se pudo iniciar sesión desde el enlace');
        return;
      }

      // Invocar accept-invite (la fuente de verdad es user_metadata).
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: {},
      });
      if (cancelled) return;
      if (error) {
        setStatus('error');
        const detail = error.message ?? 'No se pudo procesar la invitación';
        setMessage(detail);
        toast.error(detail);
        return;
      }
      const ok = (data as { ok?: boolean } | null)?.ok;
      if (!ok) {
        setStatus('error');
        setMessage('La invitación no pudo completarse.');
        toast.error('No se pudo completar la invitación');
        return;
      }
      setStatus('ok');
      toast.success('Invitación aceptada');
      await navigate({ to: '/' });
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <h1 className="text-lg font-semibold text-ink">
        {status === 'loading' && 'Aceptando invitación...'}
        {status === 'ok' && 'Invitación aceptada'}
        {status === 'error' && 'No se pudo aceptar la invitación'}
      </h1>
      <p className="text-sm text-quiet">
        {status === 'error'
          ? (message ?? 'Contacta al administrador del workspace que te invitó.')
          : 'Por favor, espera...'}
      </p>
      {search.ws && (
        <p className="text-xs text-quiet">
          Workspace ID: <code>{search.ws}</code>
        </p>
      )}
    </div>
  );
}
